import UndoIcon from "@mui/icons-material/Undo";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import type { Dayjs } from "dayjs";
import type { ChangeEvent, FC } from "react";
import type { LoyaltyPointsEventType } from "../../admin/loyalty/loyaltyApi";

const dateFormat = "DD-MM-YYYY";
const returnPointsButtonLabel = "Zwróć punkty";

export interface LoyaltyPointEvent {
  readonly id: string;
  readonly userId: string;
  readonly amount: number;
  readonly pointsAfter: number;
  readonly orderId?: { readonly value: string; readonly userFriendly: number };
  readonly rewardId?: string;
  readonly createdAt: Dayjs;
  readonly type: LoyaltyPointsEventType;
}

export interface Props {
  readonly values: LoyaltyPointEvent[];
  readonly onRefund?: (event: LoyaltyPointEvent) => void | Promise<void>;
  readonly readonly?: boolean;
  readonly page: number;
  readonly rowsPerPage: number;
  readonly count: number;
  readonly onPageChange: (newPage: number) => void;
  readonly onRowsPerPageChange: (newRowsPerPage: number) => void;
}

export const LoyaltyPointsList: FC<Props> = ({
  values,
  onRefund,
  readonly,
  page,
  rowsPerPage,
  count,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const actualCount = count;
  // nothing to show when the backend reports zero total items
  if (actualCount === 0) {
    return <></>;
  }
  const currentPage = page;
  const currentRows = rowsPerPage;

  // values already represent exactly the current page from the server
  const pagedValues = values;

  const handlePageChange = (_: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleRowsChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newSize = parseInt(e.target.value, 10);
    onRowsPerPageChange(newSize);
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Data</TableCell>
            <TableCell>Kontakt</TableCell>
            <TableCell>Typ</TableCell>
            <TableCell align="right">Punkty</TableCell>
            <TableCell align="right">Suma punktów</TableCell>
            <TableCell>Zamówienie</TableCell>
            <TableCell>Nagroda</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>

        <TableBody>
          {pagedValues.map((value) => (
            <TableRow key={value.id} hover>
              <TableCell>{value.createdAt.format(dateFormat)}</TableCell>

              <TableCell>{value.userId}</TableCell>

              <TableCell>{typeToString(value.type)}</TableCell>

              <TableCell align="right">{value.amount > 0 ? "+" + value.amount : value.amount}</TableCell>

              <TableCell align="right">{value.pointsAfter}</TableCell>

              <TableCell>{value.orderId?.userFriendly ?? "—"}</TableCell>

              <TableCell>{value.rewardId ?? "—"}</TableCell>

              <TableCell align="right">
                <Tooltip title={returnPointsButtonLabel}>
                  <span>
                    <IconButton
                      aria-label={returnPointsButtonLabel}
                      disabled={readonly || !canRefund(value)}
                      size="small"
                      onClick={() => onRefund?.(value)}
                    >
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={actualCount}
        page={currentPage}
        onPageChange={handlePageChange}
        rowsPerPage={currentRows}
        onRowsPerPageChange={handleRowsChange}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </TableContainer>
  );
};

function typeToString(type: LoyaltyPointsEventType) {
  if (type == "LOYALTY_POINTS_ADDED") {
    return "DODANE";
  }
  if (type == "LOYALTY_POINTS_REVERTED") {
    return "COFNIĘTE";
  }
  if (type == "LOYALTY_POINTS_SPENT") {
    return "WYDANE";
  }
  if (type == "LOYALTY_POINTS_REFUNDED") {
    return "ZWRÓCONE";
  }
}

function canRefund(event: LoyaltyPointEvent) {
  if (event.type == "LOYALTY_POINTS_SPENT") {
    return true;
  }
  return false;
}

export default LoyaltyPointsList;
